import React, { useState, useEffect, useRef } from 'react';

import { useSiteQuery } from '../../hooks';

import TestimonialsCarousel from './carousel';
import TestimonialsMobile from './mobile';
import query from './testimonials.graphql';
import placeholderImage from '../../images/anniversary.jpg';

import StarIcon from '../../images/icons/star.svg';

import styles from './styles.module.scss';
import cc from "classcat";

interface Props {
  fragment: TestimonialBlockFragment;
}

const Testimonials = ({ fragment }: Props) => {
  const category = fragment?.testimonialCategory?.[0]?.id || 0;
  const [{ data, fetching, error }] = useSiteQuery<TestimonialsQuery>({ query, variables: { category } });
  const categoryName = data?.category?.title;
  const items = data?.items;
  const imageLayout = fragment?.imageLayout || false;

  let headingText = fragment?.headingTextPrimary;
  if (!headingText || !headingText.length) {
      headingText = 'What do our clients say?';
  }

  // Handle loading state
  if (fetching) {
    return null;
  }

  // Handle error state
  if (error) {
    console.error('Testimonials query error:', error);
    return null;
  }

  // Handle no category or no items
  if (!categoryName || !items || items.length === 0) {
    return null;
  }

  // Filter out any null/undefined items
  const validItems = items.filter((item) => item !== null && item !== undefined);

  if (validItems.length > 0) {
    // Image Layout - New layout with images
    if (imageLayout) {
      const [currentIndex, setCurrentIndex] = useState(0);
      const [isTransitioning, setIsTransitioning] = useState(false);
      const [maxHeight, setMaxHeight] = useState<number | null>(null);
      const hasMultiple = validItems.length > 1;
      const containerRef = useRef<HTMLDivElement>(null);
      const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
      const isCalculatingRef = useRef(false);
      const intervalRef = useRef<NodeJS.Timeout | null>(null);

      // Calculate max height of all testimonial cards - only on mount and resize
      useEffect(() => {
        const calculateMaxHeight = () => {
          if (isCalculatingRef.current) return;
          if (cardRefs.current.length === 0) return;

          isCalculatingRef.current = true;

          const cards = cardRefs.current.filter(ref => ref !== null);
          if (cards.length === 0) {
            isCalculatingRef.current = false;
            return;
          }

          // Clear the max height first so cards can shrink
          setMaxHeight(null);

          // Wait for layout to settle after clearing height
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Get natural height of each card (now all at natural height)
              const heights = cards.map(card => card!.offsetHeight);

              if (heights.length > 0) {
                const newMaxHeight = Math.max(...heights);
                setMaxHeight(newMaxHeight);
              }

              isCalculatingRef.current = false;
            });
          });
        };

        // Calculate on mount and after images load
        const initialTimer = setTimeout(calculateMaxHeight, 100);
        const imageTimer = setTimeout(calculateMaxHeight, 800);

        // Recalculate on window resize with debounce
        let resizeTimer: NodeJS.Timeout;
        const handleResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(calculateMaxHeight, 300);
        };

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
          clearTimeout(initialTimer);
          clearTimeout(imageTimer);
          clearTimeout(resizeTimer);
        };
      }, []);

      // Autorotation effect - separate from height calculation
      useEffect(() => {
        if (!hasMultiple) return;

        const startInterval = () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          intervalRef.current = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
              setCurrentIndex((prevIndex) => (prevIndex + 1) % validItems.length);
              setIsTransitioning(false);
            }, 500); // Wait for fade out to complete (500ms)
          }, 15000); // Change every 15 seconds
        };

        startInterval();

        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        };
      }, [hasMultiple, validItems.length]);

      // Function to change slide and reset timer
      const changeSlide = (index: number) => {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentIndex(index);
          setIsTransitioning(false);
        }, 500);

        // Reset the autorotation timer
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        if (hasMultiple) {
          intervalRef.current = setInterval(() => {
            setIsTransitioning(true);

            setTimeout(() => {
              setCurrentIndex((prevIndex) => (prevIndex + 1) % validItems.length);
              setIsTransitioning(false);
            }, 500);
          }, 15000);
        }
      };

      const renderTestimonialCard = (item: typeof validItems[0], index: number, isVisible: boolean) => {
        if (!item) return null;

        const starRating = item.starRating || 0;
        const imageUrl = item.image?.[0]?.url;
        const title = item.title || 'Anonymous';
        const description = item.description || '';

        return (
          <div
            key={item.uid}
            ref={(el) => { cardRefs.current[index] = el; }}
            className={cc([
              styles.testimonialCard,
              !isVisible && styles.hidden,
              isVisible && isTransitioning && styles.fadeOut,
            ])}
            style={isVisible && maxHeight ? { height: `${maxHeight}px`, zIndex: 1 } : undefined}
          >
            <div className={styles.testimonialImage}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={title}
                />
              ) : (
                <img
                  src={placeholderImage}
                  alt="Testimonial"
                />
              )}
            </div>

            <div className={styles.testimonialContent}>
              {starRating > 0 && starRating <= 5 && (
                <div className={styles.starRating}>
                  {Array.from({ length: Math.min(starRating, 5) }, (_, index) => (
                    <StarIcon key={index} className={styles.star} />
                  ))}
                </div>
              )}
              {description && (
                <p className={styles.testimonialQuote}>
                  {description}
                </p>
              )}
              {title && (
                <p className={styles.testimonialName}>
                  {title}
                </p>
              )}
            </div>
          </div>
        );
      };

      return (
        <div className={cc([
                 styles.testimonialsWithImages,
                 'saSection',
             ])}
        >
          <div className="saContainer">
            <div className={styles.testimonialsContainer} ref={containerRef}>
              {validItems.map((item, index) =>
                renderTestimonialCard(item, index, index === currentIndex)
              )}

              {hasMultiple && (
                <div className={styles.testimonialDots}>
                  {validItems.map((_, index) => (
                    <button
                      key={index}
                      className={cc([
                        styles.dot,
                        index === currentIndex && styles.dotActive
                      ])}
                      onClick={() => changeSlide(index)}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Default Layout - Original carousel layout
    return (
      <div className={cc([
               styles.testimonials,
               'saSection',
           ])}
      >

        <div className="saContainer">
            <div className="sectionHeading">Testimonials</div>
            <h2 className="sectionTitle">{headingText}</h2>
          <TestimonialsCarousel category={categoryName} testimonials={items} />
          <TestimonialsMobile category={categoryName} testimonials={items} />
        </div>
      </div>

    );
  }
  return null;
};

export default Testimonials;
